import { createCart, fetchCart } from '@/api/commerce-api'
import { logger } from '@/lib/logger'
import { useCartStore, useCartStoreActions } from '@/stores/cart-store'
import { CartError } from '@/types/commerce-types'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

interface UseCartQueryOptions {
  cartKey: string | undefined
  onError?: (error: Error) => void
  enabled?: boolean
}

export const useCartQuery = ({ cartKey, onError, enabled = true }: UseCartQueryOptions) => {
  const errorCountRef = useRef(0)
  const maxConsecutiveErrors = 3
  const cartActions = useCartStoreActions()
  const hasAttemptedNewKeyRef = useRef(false)

  const query = useQuery({
    queryKey: ['cart', cartKey],
    queryFn: async () => {
      if (!cartKey) {
        throw new Error('Cart key is required')
      }

      try {
        const cart = await fetchCart(cartKey)
        // Reset error count on success
        errorCountRef.current = 0
        hasAttemptedNewKeyRef.current = false
        return cart
      } catch (error) {
        // Check if this is a cart not found error
        if (error instanceof CartError && error.isCartNotFound && !hasAttemptedNewKeyRef.current) {
          logger.info('Cart not found, creating new cart via v2 API', { oldKey: cartKey })
          hasAttemptedNewKeyRef.current = true

          try {
            // Get or create device session ID for cart persistence
            // Related: PL-500 - Session-based cart persistence
            const sessionId = useCartStore.getState().getSessionId()
            logger.info('Using session ID for cart creation', { sessionId })

            // Create new cart via v2 API with session ID
            const cartResponse = await createCart({ sessionId })
            logger.info('New cart created via v2 API', {
              newKey: cartResponse.cartKey,
              cartId: cartResponse.cartId,
              sessionId,
            })

            // Update the cart store with the new key
            cartActions.setCartKey(cartResponse.cartKey)

            // Fetch cart with the new key
            const cart = await fetchCart(cartResponse.cartKey)
            errorCountRef.current = 0
            return cart
          } catch (newKeyError) {
            logger.error('Failed to create new cart via v2 API', { error: newKeyError })
            throw new Error('Failed to create new cart')
          }
        }

        // Increment error count
        errorCountRef.current++

        // If we've hit too many consecutive errors, throw a special error
        if (errorCountRef.current >= maxConsecutiveErrors) {
          throw new CartError('Maximum consecutive errors reached for cart fetch', {
            isMaxErrorsReached: true,
            consecutiveErrors: errorCountRef.current,
          })
        }

        throw error
      }
    },
    enabled: enabled && !!cartKey,
  })

  // Handle errors
  useEffect(() => {
    if (query.error) {
      logger.error('Cart query error', {
        error: query.error.message,
        failureCount: query.failureCount,
        consecutiveErrors: errorCountRef.current,
        isMaxErrorsReached: query.error instanceof CartError ? query.error.isMaxErrorsReached : false,
      })

      // Call custom error handler if provided
      onError?.(query.error)

      // If max errors reached, we might want to take additional action
      if (query.error instanceof CartError && query.error.isMaxErrorsReached) {
        logger.error('Cart fetching disabled due to repeated failures', {
          cartKey,
          consecutiveErrors: errorCountRef.current,
        })
      }
    }
  }, [query.error, query.failureCount, cartKey, onError])

  return query
}
